// ─────────────────────────────────────────────────────────────────────────────
// Plano de Fundo Medieval Dinâmico (Parallax Ambiance)
// ─────────────────────────────────────────────────────────────────────────────
// Camada de fundo procedural (Canvas 2D) desenhada atrás da grade isométrica,
// substituindo o vazio/preto ao redor do mapa por um cenário medieval
// imersivo. Segue o mesmo padrão de Geração Procedimental Determinística já
// usado no resto do CombatGrid (montanhas, lagos, rochas) — sem depender de
// imagens externas, 100% desenhado via Canvas.
//
// Estrutura em 3 camadas reais de profundidade (Parallax):
//   1. Céu — gradiente + nuvens SOMENTE no topo, acima do horizonte.
//   2. Terreno Distante — silhuetas de montanhas/castelo baixas e árvores
//      pequenas, apoiadas exatamente na linha do horizonte.
//   3. Terreno Jogável (extensão) — o MESMO piso de grama do chão real da
//      grade, sem grade visível e sem colisão, cobrindo tudo do horizonte
//      até o rodapé — dá a ilusão de que o chão onde o personagem pisa
//      continua até a distância (fim da "ilha flutuante").
//
// Efeito Parallax: cada camada se move a uma velocidade fracionária diferente
// (`speed`) em função da rotação da câmera e do deslocamento (pan) do mapa.

export type MapBackgroundTheme = 'medieval_mountains_day' | 'dark_castle_night' | 'enchanted_forest';

/**
 * Fração vertical PADRÃO do canvas onde o "chão" do cenário de fundo encontra
 * a grade isométrica — usada apenas como fallback quando o componente ainda
 * não mediu a posição real do grid (primeiro frame). Ver `horizonY` em
 * `drawParallaxBackground`: o CombatGrid agora calcula e injeta o valor real
 * a cada frame, alinhando o horizonte com o topo de fato renderizado da
 * matriz de tiles (ponto de fuga da projeção isométrica).
 */
const HORIZON_Y_DEFAULT = 0.56;

/** Pseudo-random determinístico em [0, 1) a partir de um índice/seed. */
function hash(i: number, seed = 0): number {
  const h = Math.abs(Math.sin(i * 12.9898 + seed * 78.233) * 43758.5453);
  return h - Math.floor(h);
}

/** Converte uma cor hex (#rrggbb) em rgba() com a opacidade informada. */
function hexToRgba(hex: string, alpha: number): string {
  const p = parseInt(hex.replace('#', ''), 16);
  const r = (p >> 16) & 255, g = (p >> 8) & 255, b = p & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Executa `cb` para cada instância de um padrão repetido em `spacing`, dentro da faixa visível. */
function forEachRepeating(shiftPx: number, w: number, spacing: number, cb: (index: number, screenX: number) => void) {
  const first = Math.floor(shiftPx / spacing) - 1;
  const last  = Math.floor((shiftPx + w) / spacing) + 1;
  for (let i = first; i <= last; i++) {
    cb(i, i * spacing - shiftPx);
  }
}

/** Skyline contínuo (montanhas/colinas) — função de altura suave, sem necessidade de tiling. */
function drawSkyline(
  ctx: CanvasRenderingContext2D, w: number, h: number, shiftPx: number,
  baseY: number, amp: number, freq: number, seed: number, color: string,
) {
  ctx.beginPath();
  ctx.moveTo(0, h);
  const step = 18;
  for (let sx = 0; sx <= w + step; sx += step) {
    const wx = sx + shiftPx;
    const y = baseY - amp * (0.62 * Math.sin(wx * freq + seed) + 0.38 * Math.sin(wx * freq * 2.3 + seed * 1.6));
    ctx.lineTo(sx, y);
  }
  ctx.lineTo(w, h);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

/** Pinheiro pequeno, usado na linha de árvores distante apoiada no horizonte. */
function drawPine(ctx: CanvasRenderingContext2D, x: number, baseY: number, s: number, color: string) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x, baseY - 58 * s);
  ctx.lineTo(x - 20 * s, baseY - 14 * s);
  ctx.lineTo(x - 11 * s, baseY - 14 * s);
  ctx.lineTo(x - 24 * s, baseY + 6 * s);
  ctx.lineTo(x - 12 * s, baseY + 6 * s);
  ctx.lineTo(x - 26 * s, baseY + 26 * s);
  ctx.lineTo(x + 26 * s, baseY + 26 * s);
  ctx.lineTo(x + 12 * s, baseY + 6 * s);
  ctx.lineTo(x + 24 * s, baseY + 6 * s);
  ctx.lineTo(x + 11 * s, baseY - 14 * s);
  ctx.lineTo(x + 20 * s, baseY - 14 * s);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(x - 3 * s, baseY + 26 * s, 6 * s, 10 * s);
}

function drawCastleSilhouette(ctx: CanvasRenderingContext2D, x: number, baseY: number, s: number, color: string) {
  ctx.fillStyle = color;
  // Corpo principal
  ctx.fillRect(x - 46 * s, baseY - 60 * s, 92 * s, 60 * s);
  // Ameias (crenellations)
  const crenW = 12 * s;
  for (let cx = -46; cx < 46; cx += 24) {
    ctx.fillRect(x + cx * s, baseY - 68 * s, crenW, 10 * s);
  }
  // Torres laterais + telhados cônicos
  for (const side of [-1, 1]) {
    const tx = x + side * 52 * s;
    ctx.fillRect(tx - 12 * s, baseY - 84 * s, 24 * s, 84 * s);
    ctx.beginPath();
    ctx.moveTo(tx - 15 * s, baseY - 84 * s);
    ctx.lineTo(tx, baseY - 108 * s);
    ctx.lineTo(tx + 15 * s, baseY - 84 * s);
    ctx.closePath();
    ctx.fill();
  }
  // Torre central + telhado
  ctx.fillRect(x - 10 * s, baseY - 100 * s, 20 * s, 40 * s);
  ctx.beginPath();
  ctx.moveTo(x - 13 * s, baseY - 100 * s);
  ctx.lineTo(x, baseY - 128 * s);
  ctx.lineTo(x + 13 * s, baseY - 100 * s);
  ctx.closePath();
  ctx.fill();
}

/** Formatos de nuvem fofa — bolhas [dx, dy, r] em unidades de raio base. */
const CLOUD_SHAPES: ReadonlyArray<ReadonlyArray<readonly [number, number, number]>> = [
  [[0, 0, 1], [-1.1, 0.35, 0.7], [1.1, 0.3, 0.75], [-0.5, -0.45, 0.65], [0.55, -0.4, 0.7], [1.9, 0.55, 0.45]],
  [[0, 0, 0.9], [-0.9, 0.3, 0.6], [0.9, 0.35, 0.55], [0.2, -0.5, 0.55]],
  [[0, 0, 1.1], [-1.2, 0.4, 0.6], [1.2, 0.4, 0.7], [-0.4, -0.6, 0.6], [0.6, -0.55, 0.75], [-2, 0.6, 0.4], [2.1, 0.65, 0.45]],
];

/**
 * Nuvem fofa estilo cartoon: sombra azulada por baixo, bolhas brancas com
 * degradê (luz no alto-esquerda) e brilho no topo. `size` = raio base em px.
 */
function drawCloud(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, alpha: number, shape: number) {
  const bubbles = CLOUD_SHAPES[shape % CLOUD_SHAPES.length];
  ctx.save();
  ctx.globalAlpha = alpha;

  // Sombra/base azulada, levemente deslocada para baixo.
  ctx.fillStyle = '#9fc0ea';
  ctx.beginPath();
  for (const [dx, dy, r] of bubbles) {
    const cx = x + dx * size, cy = y + dy * size + size * 0.14;
    ctx.moveTo(cx + r * size, cy);
    ctx.arc(cx, cy, r * size, 0, Math.PI * 2);
  }
  ctx.fill();

  // Corpo: cada bolha com degradê radial próprio.
  for (const [dx, dy, r] of bubbles) {
    const cx = x + dx * size, cy = y + dy * size, rr = r * size;
    const g = ctx.createRadialGradient(cx - rr * 0.35, cy - rr * 0.4, rr * 0.1, cx, cy, rr);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(0.6, '#f1f7ff');
    g.addColorStop(1, '#d3e4f8');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, rr, 0, Math.PI * 2);
    ctx.fill();
  }

  // Brilho no topo das bolhas maiores.
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  for (const [dx, dy, r] of bubbles) {
    if (r < 0.65) continue;
    const rr = r * size;
    ctx.beginPath();
    ctx.ellipse(x + dx * size - rr * 0.3, y + dy * size - rr * 0.5, rr * 0.28, rr * 0.16, -0.4, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Camada de nuvens à deriva: cada nuvem anda devagar para a direita (com
 * volta pelo outro lado), flutua para cima/baixo e sofre parallax da câmera.
 */
function drawCloudLayer(
  ctx: CanvasRenderingContext2D, w: number, h: number, now: number, shift: number,
  opts: { count: number; seed: number; size: number; alpha: number; speed: number; parallax: number },
) {
  const margin = opts.size * 5;
  const loop = w + margin * 2;
  const t = now / 1000;
  for (let i = 0; i < opts.count; i++) {
    const size = opts.size * (0.75 + 0.5 * hash(i, opts.seed + 1));
    const baseX = hash(i, opts.seed + 2) * loop;
    const speed = opts.speed * (0.7 + 0.6 * hash(i, opts.seed + 3));
    const raw = baseX + t * speed + shift * opts.parallax;
    const x = ((raw % loop) + loop) % loop - margin;
    const bob = Math.sin(t * 0.4 + i * 1.7) * size * 0.08;
    const y = h * (0.04 + 0.92 * ((i + hash(i, opts.seed + 4) * 0.8) / opts.count)) + bob;
    drawCloud(ctx, x, y, size, opts.alpha, Math.floor(hash(i, opts.seed + 5) * CLOUD_SHAPES.length));
  }
}

/**
 * Camada 3 — Terreno Jogável (Extensão do Chão de Grama).
 *
 * Fim da "ilha flutuante": em vez de um morro genérico (oval/gradiente
 * radial) solto no meio do cenário, esta camada é um PLANO DE CHÃO real e
 * sólido — a MESMA cor de base do chão da grade (`groundColor`) — que cobre
 * inteiramente a faixa do horizonte até o rodapé do canvas. Sobre o plano,
 * uma textura em losangos ecoa (sem desenhar) a forma dos tiles isométricos
 * reais, mais densa perto da câmera e mais rarefeita perto do horizonte —
 * reforça a perspectiva de profundidade sem nunca deixar "buracos" no chão.
 * O jogador nunca pisa aqui; é somente a ilusão de que o chão onde ele está
 * continua até a distância.
 */
function drawGroundExtension(
  ctx: CanvasRenderingContext2D, w: number, h: number, shiftPx: number,
  horizonY: number, groundColor: string, tuftColor: string,
) {
  const topY = h * horizonY;
  if (topY >= h) return;
  const bandH = h - topY;

  // Base sólida e opaca — EXATAMENTE a mesma cor/tom do piso jogável real do
  // grid (Cor Única para o Chão Base), cobrindo 100% da largura. Nenhum
  // gradiente/névoa altera este tom de base — antes havia uma névoa branca
  // que clareava a faixa perto do horizonte, criando uma diferença de cor
  // perceptível bem na costura com a grade; foi removida.
  ctx.fillStyle = groundColor;
  ctx.fillRect(0, topY, w, bandH);

  // Textura de grama em losangos — ecoa a forma do tile isométrico real do
  // grid (sem desenhar a grade em si), mais densa/perto na base e rarefeita
  // perto do horizonte, como fileiras de terreno se afastando na distância.
  const tuftFillOpaque = hexToRgba(tuftColor, 1);
  const rows = 8;
  for (let r = 0; r < rows; r++) {
    const rowT = r / (rows - 1); // 0 = horizonte, 1 = base (perto da câmera)
    const rowY = topY + bandH * (rowT * rowT); // compressão perto do horizonte
    const spacing = 20 + rowT * 30;
    const rowShift = (shiftPx * (0.22 + rowT * 0.32)) % spacing;
    const alpha = 0.08 + rowT * 0.12;
    ctx.fillStyle = tuftFillOpaque;
    forEachRepeating(rowShift, w, spacing, (i, sx) => {
      const jitter = (hash(i, r * 13 + 7) - 0.5) * spacing * 0.5;
      const tw = spacing * 0.5;
      const th = tw * 0.42;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.ellipse(sx + jitter, rowY, tw, th, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  }
}

/** Linha fina de árvores pequenas apoiadas exatamente na linha do horizonte (Camada Distante). */
function drawHorizonTreeline(
  ctx: CanvasRenderingContext2D, w: number, now: number, shiftPx: number,
  horizonPxY: number, spacing: number, colorA: string, colorB: string, baseScale: number,
) {
  const shift = shiftPx * 0.34;
  forEachRepeating(shift, w, spacing, (i, sx) => {
    const s = baseScale * (0.55 + 0.4 * hash(i, 8));
    const sway = Math.sin(now / 2400 + i) * 1.4;
    drawPine(ctx, sx + sway, horizonPxY, s, hash(i, 9) > 0.5 ? colorA : colorB);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Draw por tema
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Dia: céu azul aberto com nuvens fofas à deriva — o mapa flutua como uma
 * ilha no céu. Sem horizonte/chão: o céu cobre o canvas inteiro.
 */
function drawMedievalMountainsDay(ctx: CanvasRenderingContext2D, w: number, h: number, now: number, shift: number, _horizonY: number) {
  const sky = ctx.createLinearGradient(0, 0, 0, h);
  sky.addColorStop(0, '#3f6fe0');
  sky.addColorStop(0.55, '#6fa3ee');
  sky.addColorStop(1, '#a9d4f5');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);

  // Fundo: menores, translúcidas e lentas; frente: maiores e mais rápidas.
  drawCloudLayer(ctx, w, h, now, shift, { count: 7, seed: 20, size: 22, alpha: 0.55, speed: 5,  parallax: 0.03 });
  drawCloudLayer(ctx, w, h, now, shift, { count: 5, seed: 40, size: 36, alpha: 0.95, speed: 10, parallax: 0.08 });
}

function drawDarkCastleNight(ctx: CanvasRenderingContext2D, w: number, h: number, now: number, shift: number, horizonY: number) {
  const horizonPxY0 = h * horizonY;

  // Base de segurança: chão em toda a tela ANTES do céu (ver comentário
  // equivalente em drawMedievalMountainsDay) — o céu, a seguir, fica preso
  // por `ctx.clip()` à faixa acima do horizonte e não pode vazar por baixo.
  ctx.fillStyle = '#1c2118';
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, horizonPxY0);
  ctx.clip();

  const sky = ctx.createLinearGradient(0, 0, 0, h);
  sky.addColorStop(0, '#080a1c');
  sky.addColorStop(0.55, '#171a3a');
  sky.addColorStop(1, '#2a2338');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);

  // Lua
  const moonX = w * 0.22 + shift * 0.008;
  ctx.beginPath();
  const mglow = ctx.createRadialGradient(moonX, h * 0.15, 4, moonX, h * 0.15, 60);
  mglow.addColorStop(0, 'rgba(230,235,255,0.65)');
  mglow.addColorStop(1, 'rgba(230,235,255,0)');
  ctx.fillStyle = mglow;
  ctx.arc(moonX, h * 0.15, 60, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.fillStyle = '#eef0ff';
  ctx.arc(moonX, h * 0.15, 22, 0, Math.PI * 2);
  ctx.fill();

  // ── Camada 1: Céu — estrelas confinadas acima do horizonte.
  const starMaxY = h * horizonY - 30;
  const starShift = shift * 0.03;
  forEachRepeating(starShift, w, 34, (i, sx) => {
    const y = Math.min(h * 0.05 + starMaxY * 0.55 * hash(i, 1), starMaxY);
    const tw = 0.5 + 0.5 * Math.sin(now / 900 + i * 3.1);
    ctx.fillStyle = `rgba(255,255,255,${0.25 + 0.55 * tw})`;
    ctx.fillRect(sx, y, 2, 2);
  });

  ctx.restore(); // remove o clip do céu

  // ── Camada 2: Terreno Distante — colina + castelo + árvores mortas, todos
  // apoiados exatamente na linha do horizonte.
  const horizonPxY = h * horizonY;
  drawSkyline(ctx, w, h, shift * 0.14, horizonPxY - h * 0.05, h * 0.10, 0.0026, 2.2, '#161832');
  const castleShift = shift * 0.18;
  forEachRepeating(castleShift, w, 520, (i, sx) => {
    drawCastleSilhouette(ctx, sx, horizonPxY, 0.75 + 0.3 * hash(i, 4), '#0d0e22');
  });
  drawHorizonTreeline(ctx, w, now, shift, horizonPxY, 110, '#0c0c16', '#0c0c16', 0.42);

  // ── Camada 3: Terreno Jogável — extensão sólida do chão escuro.
  drawGroundExtension(ctx, w, h, shift, horizonY, '#1c2118', '#0e100b');

  // Névoa rasteira — sobre o chão, logo abaixo do horizonte (atmosfera extra).
  const fogShift = shift * 0.34;
  const fogBaseY = horizonPxY + h * 0.02;
  for (let i = 0; i < 3; i++) {
    const y = fogBaseY + h * i * 0.03;
    const alpha = 0.08 + i * 0.03;
    ctx.fillStyle = `rgba(200,200,220,${alpha})`;
    ctx.beginPath();
    const off = (fogShift * (0.6 + i * 0.15)) % w;
    ctx.ellipse(w / 2 - off, y, w * 0.7, 16, 0, 0, Math.PI * 2);
    ctx.ellipse(w / 2 - off + w, y, w * 0.7, 16, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawEnchantedForest(ctx: CanvasRenderingContext2D, w: number, h: number, now: number, shift: number, horizonY: number) {
  const horizonPxY = h * horizonY;

  // Base de segurança: chão em toda a tela ANTES do céu (ver comentário
  // equivalente em drawMedievalMountainsDay).
  ctx.fillStyle = '#22301f';
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, horizonPxY);
  ctx.clip();

  const sky = ctx.createLinearGradient(0, 0, 0, h);
  sky.addColorStop(0, '#241a3d');
  sky.addColorStop(0.5, '#3a2a5c');
  sky.addColorStop(1, '#1c3b34');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);

  // ── Camada 1: Céu — névoa mágica alta, confinada acima do horizonte.
  drawSkyline(ctx, w, h, shift * 0.08, horizonPxY - h * 0.14, h * 0.06, 0.0018, 6.4, 'rgba(120,90,180,0.18)');

  ctx.restore(); // remove o clip do céu

  // ── Camada 2: Terreno Distante — árvores mágicas apoiadas no horizonte.
  drawSkyline(ctx, w, h, shift * 0.16, horizonPxY, h * 0.02, 0.02, 1.1, '#20233a');
  drawHorizonTreeline(ctx, w, now, shift, horizonPxY, 56, '#232a44', '#1c2338', 0.55);

  // ── Camada 3: Terreno Jogável — extensão sólida do chão mágico.
  drawGroundExtension(ctx, w, h, shift, horizonY, '#22301f', '#182212');

  // Vaga-lumes / partículas mágicas — pairam sobre o chão jogável.
  const fireflyShift = shift * 0.5;
  forEachRepeating(fireflyShift, w, 90, (i, sx) => {
    const y = horizonPxY + (h - horizonPxY) * (0.10 + 0.55 * hash(i, 4));
    const pulse = 0.4 + 0.6 * Math.abs(Math.sin(now / 800 + i * 2.7));
    const drift = Math.sin(now / 1500 + i) * 10;
    ctx.beginPath();
    const g = ctx.createRadialGradient(sx + drift, y, 0, sx + drift, y, 6);
    g.addColorStop(0, `rgba(190,255,150,${0.8 * pulse})`);
    g.addColorStop(1, 'rgba(190,255,150,0)');
    ctx.fillStyle = g;
    ctx.arc(sx + drift, y, 6, 0, Math.PI * 2);
    ctx.fill();
  });

  // Névoa rasteira translúcida — do horizonte até o rodapé.
  ctx.fillStyle = 'rgba(140,120,200,0.10)';
  ctx.fillRect(0, horizonPxY + h * 0.04, w, h - (horizonPxY + h * 0.04));
}

const THEME_DRAWERS: Record<MapBackgroundTheme, typeof drawMedievalMountainsDay> = {
  medieval_mountains_day: drawMedievalMountainsDay,
  dark_castle_night:      drawDarkCastleNight,
  enchanted_forest:       drawEnchantedForest,
};

/**
 * Desenha o plano de fundo com efeito Parallax na camada atrás da grade.
 * `shiftPx` deve combinar a rotação da câmera + o pan/scroll do mapa numa
 * única grandeza em pixels "de mundo" — cada camada interna aplica sua
 * própria fração de velocidade sobre esse valor.
 *
 * `horizonY` (fração 0–1 da altura do canvas) alinha a linha do horizonte do
 * cenário de fundo com o ponto onde a grade isométrica real termina/começa —
 * o CombatGrid mede a posição real do canvas do grid dentro do wrapper a
 * cada frame e injeta o valor aqui, em vez de uma constante fixa arbitrária,
 * fazendo a projeção convergir visualmente para o mesmo "ponto de fuga".
 */
export function drawParallaxBackground(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  now: number,
  shiftPx: number,
  theme: MapBackgroundTheme = 'medieval_mountains_day',
  horizonY: number = HORIZON_Y_DEFAULT,
) {
  const draw = THEME_DRAWERS[theme] ?? drawMedievalMountainsDay;
  // Limite inferior bem mais baixo (0.08) que antes (0.30): a paisagem
  // distante deve poder subir até quase o topo do canvas em mapas grandes ou
  // muito ampliados, ficando "logo acima do topo do mapa jogável" em vez de
  // presa numa faixa fixa que a empurrava para dentro da área da grade.
  const clampedHorizonY = Math.min(0.85, Math.max(0.08, horizonY));
  draw(ctx, w, h, now, shiftPx, clampedHorizonY);
}
